import { Box } from '@chakra-ui/react';
import React, { useCallback, useRef, useState, useMemo, useEffect } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { VirtualFilterItem } from './VirtualFilterItem';
import { menuMaxHeight } from '@duke-hq/ui/constants/scalable';

interface VirtualizedFilterProps {
  items: {
    id: string;
    value: string;
    label: string;
    element?: React.ReactElement;
  }[];
  selectedItems: string[];
  onCheckboxChange: (id: string) => void;
  isAtLimit?: boolean;
}

export const VirtualizedFilterList = React.memo<VirtualizedFilterProps>(
  ({ items, selectedItems, onCheckboxChange, isAtLimit = false }) => {
    const [renderList, setRenderList] = useState(false);
    const parentRef = useRef<HTMLDivElement>(null);

    const dynamicHeight = useMemo(() => {
      const itemHeight = 33;
      const maxItemsVisible = 10;

      return Math.min(items.length * itemHeight, maxItemsVisible * itemHeight);
    }, [items.length]);

    const virtualizer = useVirtualizer({
      count: items?.length,
      getScrollElement: () => parentRef.current,
      estimateSize: useCallback(() => 36, []),
    });

    const virtualItems = virtualizer.getVirtualItems();

    useEffect(() => {
      if (parentRef.current) {
        setRenderList(true);
      }
    }, [parentRef]);

    return (
      <Box
        ref={parentRef}
        maxHeight={menuMaxHeight}
        width="full"
        overflowY="auto"
        height={`${dynamicHeight}px`}
      >
        {renderList ? (
          <Box height={virtualizer.getTotalSize()} width={'full'} position="relative">
            {virtualItems.map((virtualRow) => (
              <Box
                listStyleType="none"
                p={0}
                m={0}
                key={virtualRow.key}
                width="100%"
                position="absolute"
                top={0}
                left={0}
                transform={`translateY(${virtualRow.start}px)`}
              >
                <VirtualFilterItem
                  key={virtualRow.key}
                  virtualRow={virtualRow}
                  item={items[virtualRow.index]}
                  measureElement={virtualizer.measureElement}
                  selectedItems={selectedItems}
                  onCheckboxChange={onCheckboxChange}
                  isAtLimit={isAtLimit}
                />
              </Box>
            ))}
          </Box>
        ) : null}
      </Box>
    );
  },
);

VirtualizedFilterList.displayName = 'VirtualizedFilterList';
